import { useEffect, useState } from "react";
import { Crown, X, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { trackAuthed } from "../lib/analytics";

// Bénéfices marketing (copie FR uniquement — les PRIX viennent toujours du backend).
const PLAN_BENEFITS = {
  PREMIUM: ["Projets illimités", "Coach IA avancé", "Smart Release Planner", "Content Studio & Analytics avancées", "Preuves audio / vidéo / lien"],
  PRO: ["Tout Premium inclus", "Growth & Audience", "Marketing Autopilot", "Connecteurs externes", "IA avancée & automatisations"],
};
const PLAN_NAME = { PREMIUM: "Premium", PRO: "Pro" };

// Modale d'upgrade réutilisable. S'ouvre uniquement quand une limite/fonctionnalité
// nécessitant un plan supérieur est rencontrée (event cehnura:paywall), jamais de façon agressive.
export function PaywallGate() {
  const [info, setInfo] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Pas de fetch au montage : PaywallGate vit au-dessus du Router et monte sur /login (401).
  // On charge les prix RÉELS à la demande, quand la modale est déclenchée (utilisateur authentifié).
  useEffect(() => {
    const handler = async (e) => {
      const d = e.detail || {};
      const plan = d.upgrade_required;
      if (!plan) return;
      trackAuthed("upgrade_modal_view", { plan, feature: d.feature || "" });
      try {
        const r = await api.get("/subscription");
        setPricing(r.data?.pricing || null);
      } catch { /* garde le pricing courant */ }
      setInfo({ plan, feature: d.feature || d.message || "", message: d.reason || d.message || `Cette fonctionnalité nécessite le plan ${PLAN_NAME[plan] || plan}.` });
    };
    window.addEventListener("cehnura:paywall", handler);
    return () => window.removeEventListener("cehnura:paywall", handler);
  }, []);

  if (!info) return null;

  const plan = info.plan;
  const price = pricing?.[plan];
  const benefits = PLAN_BENEFITS[plan] || [];

  const upgrade = async () => {
    trackAuthed("upgrade_click", { plan });
    setBusy(true);
    try {
      trackAuthed("checkout_started", { plan, source: "upgrade_modal" });
      const { data } = await api.post("/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      setBusy(false);
      navigate("/subscription");
      setInfo(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="upgrade-modal">
      <div className="w-full max-w-sm bg-[#18181E] border border-amber-500/40 rounded-3xl p-6 relative glow-gold">
        <button onClick={() => setInfo(null)} data-testid="upgrade-close" className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
        <div className="w-12 h-12 rounded-2xl gold-gradient flex items-center justify-center mb-4"><Crown className="w-6 h-6 text-black" /></div>
        <h2 className="font-display text-xl font-bold mb-1" data-testid="upgrade-title">Passe à {PLAN_NAME[plan] || plan}</h2>
        <p className="text-sm text-zinc-400 mb-4" data-testid="upgrade-message">{info.message}</p>

        {benefits.length > 0 && (
          <ul className="space-y-1.5 mb-4" data-testid="upgrade-benefits">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> {b}</li>
            ))}
          </ul>
        )}

        {price && (
          <p className="font-display text-2xl font-extrabold mb-4" data-testid="upgrade-price">
            {price.amount.toFixed(2).replace(".", ",")}€<span className="text-sm text-zinc-500 font-normal">/mois</span>
          </p>
        )}

        <button onClick={upgrade} disabled={busy} data-testid="upgrade-cta"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `Débloquer avec ${PLAN_NAME[plan] || plan}`}
        </button>
        <button onClick={() => setInfo(null)} data-testid="upgrade-later" className="w-full h-10 mt-2 text-sm text-zinc-500 hover:text-zinc-300">Plus tard</button>
      </div>
    </div>
  );
}
